// Componente de seguridad para mostrar comodidades sin errores
import React from 'react'
import { useTheme } from '../../hooks/useTheme'

interface SafeComodidadesProps {
  comodidades: any[]
  maxItems?: number
  className?: string
}

const SafeComodidades: React.FC<SafeComodidadesProps> = ({
  comodidades = [],
  maxItems = 3,
  className = ''
}) => {
  const { colors } = useTheme()

  // Función helper para convertir hex a rgb
  const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        }
      : null
  }

  // Función para extraer el nombre y icono de manera segura
  const getComodidadInfo = (comodidad: any): { nombre: string; icono?: string } => {
    if (typeof comodidad === 'string') {
      // Si es string, mantener como nombre sin icono
      return { nombre: comodidad }
    }

    if (typeof comodidad === 'object' && comodidad !== null) {
      const nombre = comodidad.nombre || comodidad.title || comodidad.label || 'Comodidad'
      const icono = comodidad.icono || comodidad.icon || undefined
      return { nombre, icono }
    }

    // Fallback para casos extraños
    return { nombre: String(comodidad) || 'Comodidad' }
  }

  if (!Array.isArray(comodidades) || comodidades.length === 0) {
    return (
      <div className={className}>
        <span className="text-xs text-gray-400 italic">Sin comodidades</span>
      </div>
    )
  }

  // Convertir color primario a RGB para background con alpha
  const rgbPrimary = hexToRgb(colors.primary)
  const backgroundColor = rgbPrimary
    ? `rgba(${rgbPrimary.r}, ${rgbPrimary.g}, ${rgbPrimary.b}, 0.1)`
    : colors.primaryLight

  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {comodidades.slice(0, maxItems).map((comodidad, i) => {
        const { nombre, icono } = getComodidadInfo(comodidad)
        return (
          <span
            key={`${nombre}-${i}`}
            className="inline-flex items-center text-xs rounded-full px-3 py-1.5"
            style={{
              backgroundColor,
              color: colors.primary,
              border: `1px solid ${colors.primary}33`,
              gap: '0.375rem' // 6px - más ajustado que gap-1.5
            }}
          >
            {icono && (
              <span
                style={{
                  fontSize: '16px',
                  lineHeight: '1',
                  display: 'inline-block',
                  verticalAlign: 'middle'
                }}
              >
                {icono}
              </span>
            )}
            <span style={{ whiteSpace: 'nowrap' }}>{nombre}</span>
          </span>
        )
      })}
      {comodidades.length > maxItems && (
        <span
          className="inline-flex items-center text-xs font-medium px-3 py-1.5 rounded-full"
          style={{ color: colors.primary }}
        >
          +{comodidades.length - maxItems} más
        </span>
      )}
    </div>
  )
}

export default SafeComodidades